import { FC, useCallback } from "react";

import Feature from "src/js/stores/user/Feature";
import { isEqual } from "lodash";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import {
  ComparablesFilterOptions,
  HMLRComparablesFilterOptions,
  HomeTrackComparablesFilterOptions,
  LegacyComparablesFilterOptions,
} from "../../typings/Comparables";
import { Button } from "react-migration/components/Button/Button";
import { FiltersProps } from "./Filters";
import { HmlrFilterFields, HmlrFilters } from "./HmlrFilters";
import { HometrackFilterFields, HometrackFilters } from "./HometrackFilters";
import { getDefaultFilters, getDefaultHmlrFilters, defaultHometrackFilters } from "../../util/defaultFilters";
import { DCSCondition } from "react-migration/lib/typings/comparables/dcs";
import { LegacyComparablesFilterFields, LegacyFilters } from "./LegacyFilters";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import hasFeature from "src/js/stores/user/actions/hasFeature";

export const useFiltersUpdate = (
  filters: ComparablesFilterOptions,
  setFiltersFn: (filters: ComparablesFilterOptions) => void,
  selectedDCSConditions: DCSCondition[],
  eventTracking?: (values: { filterName: string; filterState: unknown }) => void
) => {
  return useCallback(
    (val: Partial<ComparablesFilterOptions>) => {
      if (eventTracking) {
        for (const [key, value] of Object.entries(val)) {
          eventTracking({ filterName: key, filterState: value });
        }
      }
      const updatedFilters = {
        ...filters,
        dcsConditions: selectedDCSConditions,
        ...val,
      } as ComparablesFilterOptions;
      if (!isEqual(updatedFilters, filters)) {
        setFiltersFn(updatedFilters);
      }
    },
    [filters, setFiltersFn, eventTracking, selectedDCSConditions]
  );
};

export type ComparablesFilterFields = HmlrFilterFields | HometrackFilterFields;

export interface FiltersWithClearProps
  extends Omit<
    FiltersProps<ComparablesFilterFields, ComparablesFilterOptions>,
    "updateFilters" | "selectedDCSConditions"
  > {
  filters: ComparablesFilterOptions;
  disabled?: boolean;
  // Either use setFilters (legacy callback) or provide both specific setter functions
  setHmlrFilters?: (filters: HMLRComparablesFilterOptions) => void;
  setHometrackFilters?: (filters: HomeTrackComparablesFilterOptions) => void;
  setFilters?: (filters: LegacyComparablesFilterOptions) => void;
  dataSource: "hmlr" | "hometrack";
  eventTracking?: (values: { filterName: string; filterState: unknown }) => void;
}

export const FiltersWithClear: FC<FiltersWithClearProps> = ({
  disabled,
  filters,
  setFilters,
  setHmlrFilters,
  setHometrackFilters,
  eventTracking,
  dataSource,
  ...props
}) => {
  const { t } = useTranslation();
  const hometrackv2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);
  const selectedDCSConditions = filters.dcsConditions;

  // Determine which setter function to use based on dataSource and available props
  const setFiltersFn = useCallback(
    (updatedFilters: ComparablesFilterOptions) => {
      if (!hometrackv2 && setFilters) {
        setFilters(updatedFilters as LegacyComparablesFilterOptions);
        return;
      } else if (dataSource === "hmlr" && setHmlrFilters) {
        setHmlrFilters(updatedFilters as HMLRComparablesFilterOptions);
      } else if (dataSource === "hometrack" && setHometrackFilters) {
        setHometrackFilters(updatedFilters as HomeTrackComparablesFilterOptions);
      } else if (setFilters) {
        // Fallback if setFilters is provided and none of the above conditions match,
        // it will be a custom setter function like updateFilters from useComparablesFilterState
        (setFilters as (filters: ComparablesFilterOptions) => void)(updatedFilters);
      }
    },
    [dataSource, setFilters, setHmlrFilters, setHometrackFilters, hometrackv2]
  );

  const updateFilters = useFiltersUpdate(filters, setFiltersFn, selectedDCSConditions || [], eventTracking);

  return (
    <div data-testid="filters" className={`${disabled ? "cursor-not-allowed " : ""}relative`}>
      <div className="p-2 flex justify-end gap-2">
        <Button
          variant="ghost"
          disabled={disabled}
          onClick={() => {
            const defaults = !hometrackv2
              ? getDefaultFilters()
              : dataSource === "hmlr"
              ? getDefaultHmlrFilters()
              : defaultHometrackFilters;
            updateFilters(defaults);
          }}
        >
          {t("comparables.filters.set_default")}
        </Button>
      </div>
      <FiltersContent
        disabled={disabled}
        filters={filters}
        updateFilters={updateFilters}
        selectedDCSConditions={selectedDCSConditions || []}
        dataSource={dataSource}
        {...props}
      />
    </div>
  );
};

interface FiltersContentProps extends FiltersProps<ComparablesFilterFields, ComparablesFilterOptions> {
  dataSource: "hmlr" | "hometrack";
}

export const FiltersContent: FC<FiltersContentProps> = (props) => {
  const { dataSource } = props;
  const hometrackv2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);
  const usAccess = hasFeature(Feature.usAccess);

  if (!hometrackv2 || usAccess) {
    const legacyProps: FiltersProps<LegacyComparablesFilterFields, LegacyComparablesFilterOptions> = {
      ...props,
      filters: props.filters as LegacyComparablesFilterOptions,
      disabledFields: props.disabledFields as Set<LegacyComparablesFilterFields> | undefined,
      hiddenFields: props.hiddenFields as Set<LegacyComparablesFilterFields> | undefined,
    };

    return <LegacyFilters {...legacyProps} />;
  }

  if (dataSource === "hmlr") {
    const hmlrProps = {
      ...props,
      filters: props.filters as HMLRComparablesFilterOptions,
      disabledFields: props.disabledFields as Set<HmlrFilterFields> | undefined,
      hiddenFields: props.hiddenFields as Set<HmlrFilterFields> | undefined,
    };
    return <HmlrFilters {...hmlrProps} />;
  } else {
    const hometrackProps = {
      ...props,
      filters: props.filters as HomeTrackComparablesFilterOptions,
      disabledFields: props.disabledFields as Set<HometrackFilterFields> | undefined,
      hiddenFields: props.hiddenFields as Set<HometrackFilterFields> | undefined,
    };
    return <HometrackFilters {...hometrackProps} />;
  }
};
